/**
 * ML Readiness Results - Station Results Panel (Sprint 9)
 *
 * Dedicated results view for the ML Readiness station showing:
 * - Overall ML Readiness Score (0-100)
 * - Three dimension scores: Sufficiency, Bias Risk, Feature Engineering
 * - Class imbalance visualization
 * - Feature correlation highlights
 * - Bias risk indicators
 * - ML-specific recommendations
 *
 * Design Decision: ML Readiness has its own panel because Data Scientists
 * need different metrics than Data Quality (which targets Data Engineers).
 *
 * @see Documentation/Development/Design Docs/UX_Architecture_Redesign_v1.0.md Section 11.2
 */

import React, { useEffect, useState } from 'react';
import './ml-readiness-results.css';
import { dispatchMLMetricClick } from '../../types/mlReadinessMetric';

// ============================================================================
// TYPES (mirroring server types)
// ============================================================================

interface MLReadinessBundle {
  version: string;
  jobId: string;
  datasetId: string;
  timestamp: string;
  overallReadiness: number;
  /** Overall score including Phase 3 analysis (may differ from overallReadiness) */
  score?: number;
  summary: string;
  sufficiency: MLSufficiencyScore;
  bias: BiasDetectionScore;
  featureEngineering: FeatureEngineeringReadiness;
  privacy?: PrivacyRiskScore;  // AIDRIN GAP-06
  recommendations: MLReadinessRecommendation[];
}

// AIDRIN GAP-06: Privacy Risk Assessment
interface PrivacyRiskScore {
  overall: number;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  kAnonymity: {
    k: number;
    pass: boolean;
    threshold: number;
    uniqueCombinations: number;
    columnsUsed: string[];
    interpretation: string;
  };
  quasiIdentifiers: {
    detected: Array<{
      column: string;
      type: 'direct' | 'quasi' | 'sensitive' | 'non_sensitive';
      riskScore: number;
      uniqueRatio: number;
    }>;
    highRiskColumns: string[];
    mediumRiskColumns: string[];
    lowRiskColumns: string[];
  };
  reidentificationRisk: {
    prosecutorRisk: number;
    journalistRisk: number;
    marketerRisk: number;
    overallRisk: number;
    interpretation: string;
  };
  recommendations: string[];
}

interface MLSufficiencyScore {
  overall: number;
  volume: { score: number; sampleCount: number; status: string };
  coverage: { score: number; completeness: number; status: string };
  balance: { score: number; balanceRatio: number; status: string; classDistribution?: Record<string, number> };
  recommendations: string[];
}

interface BiasDetectionScore {
  overall: number;
  riskLevel: 'low' | 'medium' | 'high';
  demographics: Record<string, { status: string; dominantCategory: string; dominantPercentage: number }>;
  outcomeBias: { detected: boolean; maxRiskRatio: number };
  featureBias: { proxyVariablesDetected: string[] };
  // AIDRIN GAP-05: Total Statistical Distance for fairness
  tsd?: {
    value: number;
    group1_name: string;
    group2_name: string;
    interpretation: 'no_bias' | 'slight_bias' | 'moderate_bias' | 'severe_bias';
  };
  // Phase 3: Discrimination Index (CDD analysis)
  discriminationIndex?: {
    maxCDD: number;
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    significantAttributes: string[];
    protectedAttributesAnalyzed: string[];
    jsOverall?: number;
  };
  recommendations: string[];
}

interface FeatureEngineeringReadiness {
  overall: number;
  dataTypes: {
    numeric: { count: number; columns: string[] };
    categorical: { count: number; columns: string[] };
    text: { count: number; columns: string[] };
    temporal: { count: number; columns: string[] };
    unstructured: { count: number; columns: string[] };
  };
  issues: Array<{ column: string; issueType: string; severity: string; description: string }>;
  highCorrelationPairs: Array<{ column1: string; column2: string; correlation: number }>;
  // AIDRIN GAP-03: Feature importance
  featureImportance?: {
    features: Array<{ column: string; variance_importance: number }>;
    top_features: string[];
    recommendation: string;
  };
  recommendations: string[];
}

interface MLReadinessRecommendation {
  title: string;
  description: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  category: 'sufficiency' | 'bias' | 'feature_engineering' | 'privacy';
}

interface MLReadinessResultsProps {
  jobId: string;
  onError?: (error: string) => void;
  onRetry?: () => void;
}

// ============================================================================
// PHASE 3 TYPES
// ============================================================================

interface Phase3Summary {
  tieredAnalysisStatus?: 'not_started' | 'tier1_complete' | 'tier2_complete' | 'tier3_complete';
  sampleSizeAdequate?: boolean;
  factor50Score?: number;
  powerAnalysisScore?: number;
  classSeparabilityScore?: number;
  discriminationIndexScore?: number;
  mislabelDetectionRun?: boolean;
  suspectedMislabelsCount?: number;
  dataPointImpactRun?: boolean;
  highInfluenceCount?: number;
}

interface _SampleSizeAdequacy {
  factor50: {
    n_samples: number;
    n_features: number;
    factor50_minimum: number;
    adequacy_ratio: number;
    is_adequate: boolean;
    interpretation: string;
    recommendations: string[];
  };
  power_analysis: {
    observed_effect_size: number;
    current_power: number;
    minimum_sample_size: number;
    is_powered: boolean;
    interpretation: string;
  };
  overall_score: number;
  overall_adequate: boolean;
  summary: string;
}

interface ClassSeparabilityData {
  overallScore?: number;
  fisherRatioAvg?: number;
  rValueScore?: number;
  separability?: Array<{
    class_pair: string;
    r_value: number;
    fisher_ratio: number;
    interpretation: string;
  }>;
}

interface DiscriminationIndexData {
  overallScore?: number;
  maxCDD?: number;
  riskLevel?: 'low' | 'medium' | 'high' | 'critical';
  significantDisparities?: Array<{
    protected_attribute: string;
    cdd_value: number;
    p_value: number;
    significant: boolean;
  }>;
}

interface DataPointImpactData {
  highInfluenceCount?: number;
  highInfluencePoints?: Array<{
    row_index: number;
    influence_score: number;
    shapley_value: number;
  }>;
}

interface MislabelData {
  cohensKappa?: number;
  mislabels?: Array<{
    row_index: number;
    original_label: string;
    predicted_label: string;
    confidence: number;
  }>;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const MLReadinessResults: React.FC<MLReadinessResultsProps> = ({
  jobId,
  onError,
  onRetry,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<MLReadinessBundle | null>(null);

  // Phase 3 state
  const [phase3Summary, setPhase3Summary] = useState<Phase3Summary | null>(null);
  const [classSeparability, setClassSeparability] = useState<ClassSeparabilityData | null>(null);
  const [discriminationIndex, setDiscriminationIndex] = useState<DiscriminationIndexData | null>(null);
  const [dataPointImpact, setDataPointImpact] = useState<DataPointImpactData | null>(null);
  const [mislabelData, setMislabelData] = useState<MislabelData | null>(null);
  const [showPhase3Details, setShowPhase3Details] = useState(false);
  // DC-880: "Include Deep Analysis" checkbox state
  const [includeDeepAnalysis, setIncludeDeepAnalysis] = useState(false);

  useEffect(() => {
    fetchResults();
    fetchPhase3Data();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchResults and fetchPhase3Data are defined in component
  }, [jobId]);

  // Mount the ML Metric Drawer for this job
  useEffect(() => {
    if (jobId && typeof window.mountMLMetricDrawer === 'function') {
      window.mountMLMetricDrawer(jobId);
      console.log('[MLReadinessResults] Mounted ML Metric Drawer for job:', jobId);
    }
    return () => {
      // Unmount the drawer when component unmounts
      if (typeof window.unmountMLMetricDrawer === 'function') {
        window.unmountMLMetricDrawer();
      }
    };
  }, [jobId]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/ml-readiness/${jobId}/results`);

      if (!response.ok) {
        throw new Error(`Failed to fetch ML readiness results: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.status === 'failed') {
        throw new Error(data.error || 'ML readiness analysis failed');
      }

      if (data.status === 'processing') {
        setTimeout(fetchResults, 2000);
        return;
      }

      setResults(data.bundle || data);
      setLoading(false);
    } catch (err: unknown) {
      console.error('[MLReadinessResults] Failed to fetch results:', err);
      const errMessage = err instanceof Error ? err.message : 'Failed to fetch results';
      setError(errMessage);
      setLoading(false);
      onError?.(errMessage);
    }
  };

  // Fetch Phase 3 advanced analysis data
  const fetchPhase3Data = async () => {
    try {
      // Fetch Phase 3 summary
      const summaryRes = await fetch(`/api/ml-readiness/${jobId}/phase3-summary`);
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        if (summaryData.success && summaryData.summary) {
          // Phase-3 data present means deep analysis was run at creation — show
          // the section by default instead of hiding it behind the toggle (the
          // user already opted in; the DC-880 default was off).
          setIncludeDeepAnalysis(true);
          setPhase3Summary(summaryData.summary);
        }
      }

      // Fetch class separability if available
      const classRes = await fetch(`/api/ml-readiness/${jobId}/class-separability`);
      if (classRes.ok) {
        const classData = await classRes.json();
        if (classData.success) {
          setIncludeDeepAnalysis(true);
          setClassSeparability({
            overallScore: classData.overallScore,
            fisherRatioAvg: classData.fisherRatioAvg,
            rValueScore: classData.rValueScore,
            separability: classData.separability
          });
        }
      }

      // Fetch discrimination index if available
      const discRes = await fetch(`/api/ml-readiness/${jobId}/discrimination-index`);
      if (discRes.ok) {
        const discData = await discRes.json();
        if (discData.success) {
          setDiscriminationIndex({
            overallScore: discData.overallScore,
            maxCDD: discData.maxCDD,
            riskLevel: discData.riskLevel,
            significantDisparities: discData.significantDisparities
          });
        }
      }

      // Fetch data point impact if available
      const impactRes = await fetch(`/api/ml-readiness/${jobId}/data-point-impact`);
      if (impactRes.ok) {
        const impactData = await impactRes.json();
        if (impactData.success) {
          setDataPointImpact({
            highInfluenceCount: impactData.highInfluenceCount,
            highInfluencePoints: impactData.highInfluencePoints
          });
        }
      }

      // Fetch mislabel data if available
      const mislabelRes = await fetch(`/api/ml-readiness/${jobId}/mislabels`);
      if (mislabelRes.ok) {
        const mislabelResponse = await mislabelRes.json();
        if (mislabelResponse.success) {
          setMislabelData({
            cohensKappa: mislabelResponse.cohensKappa,
            mislabels: mislabelResponse.mislabels
          });
        }
      }
    } catch (err) {
      // Phase 3 data is optional, don't fail the component
      console.warn('[MLReadinessResults] Phase 3 data fetch failed (non-critical):', err);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'score-good';
    if (score >= 40) return 'score-fair';
    return 'score-poor';
  };

  const getRiskColor = (level: string): string => {
    if (level === 'low') return 'risk-low';
    if (level === 'medium') return 'risk-medium';
    return 'risk-high';
  };

  const getPrivacyRiskColor = (level: string): string => {
    if (level === 'low') return 'risk-low';
    if (level === 'medium') return 'risk-medium';
    if (level === 'high') return 'risk-high';
    return 'risk-critical';
  };

  const getPriorityIcon = (priority: string): string => {
    switch (priority) {
      case 'critical': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'sufficiency': return '📊';
      case 'bias': return '⚖️';
      case 'feature_engineering': return '🔧';
      case 'privacy': return '🔒';
      default: return '📋';
    }
  };

  const _getTierStatusLabel = (status?: string): { label: string; color: string } => {
    switch (status) {
      case 'tier3_complete': return { label: 'Deep Analysis Complete', color: 'tier-complete' };
      case 'tier2_complete': return { label: 'Standard Analysis Complete', color: 'tier-partial' };
      case 'tier1_complete': return { label: 'Quick Scan Complete', color: 'tier-basic' };
      default: return { label: 'Not Started', color: 'tier-none' };
    }
  };

  const getKappaInterpretation = (kappa: number): string => {
    if (kappa >= 0.81) return 'Almost Perfect';
    if (kappa >= 0.61) return 'Substantial';
    if (kappa >= 0.41) return 'Moderate';
    if (kappa >= 0.21) return 'Fair';
    return 'Poor';
  };

  const hasPhase3Data = (): boolean => {
    return !!(phase3Summary || classSeparability || discriminationIndex || dataPointImpact || mislabelData);
  };

  // ============================================================================
  // RENDER: LOADING
  // ============================================================================

  if (loading) {
    return (
      <div className="ml-readiness-results loading">
        <div className="ml-card">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Analyzing ML readiness...</p>
            <small>Evaluating sufficiency, bias, and feature engineering</small>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER: ERROR
  // ============================================================================

  if (error) {
    return (
      <div className="ml-readiness-results error">
        <div className="ml-card">
          <div className="error-state">
            <span className="error-icon">&#9888;</span>
            <h3>Failed to Load ML Readiness Results</h3>
            <p>{error}</p>
            {onRetry && (
              <button className="btn btn-primary" onClick={onRetry}>
                Retry Analysis
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return null;
  }

  // ============================================================================
  // RENDER: RESULTS
  // ============================================================================

  // FIX (2026-01-20): Fall back to 'score' for backwards compatibility
  // Older journeys stored the overall score as 'score', newer ones have 'overallReadiness'
  const { sufficiency, bias, featureEngineering, privacy, recommendations } = results;

  // DEBUG: Log what we receive to trace the 50 vs 74 mismatch
  console.log('[MLReadinessResults] DEBUG - results keys:', Object.keys(results));
  console.log('[MLReadinessResults] DEBUG - results.overallReadiness:', (results as unknown as { overallReadiness?: number }).overallReadiness);
  console.log('[MLReadinessResults] DEBUG - results.score:', (results as unknown as { score?: number }).score);
  console.log('[MLReadinessResults] DEBUG - full results:', JSON.stringify(results, null, 2).substring(0, 500));

  // Use 'score' as the authoritative value - it includes Phase 3 analysis
  // The old 'overallReadiness' may have been calculated from component averages only
  const overallReadiness = results.score ?? results.overallReadiness ?? 0;

  return (
    <div className="ml-readiness-results">
      {/* Header with Overall Score */}
      <div className="ml-header">
        <div className="ml-header-left">
          <h2>
            <span className="header-icon">🤖</span>
            ML Readiness Assessment
          </h2>
          <p className="ml-subtitle">
            Evaluates dataset suitability for machine learning applications
          </p>
        </div>
        <div className="ml-overall-score">
          <div className={`score-circle ${getScoreColor(overallReadiness)}`}>
            <span className="score-value">{Math.round(overallReadiness)}</span>
            <span className="score-label">/ 100</span>
          </div>
          <span className="score-title">
            Overall Readiness
            <button
              type="button"
              className="ml-metric-info-btn ml-metric-info-btn--inline"
              onClick={() => dispatchMLMetricClick('overall_readiness', overallReadiness)}
              aria-label="Learn about Overall ML Readiness"
              title="What is Overall ML Readiness?"
            >
              ?
            </button>
          </span>
        </div>
      </div>

      {/* Three Dimension Cards */}
      <div className="ml-dimensions">
        {/* Sufficiency Card */}
        <div className="dimension-card">
          <div className="dimension-header">
            <span className="dimension-icon">📊</span>
            <h3>
              Data Sufficiency
              <button
                type="button"
                className="ml-metric-info-btn"
                onClick={() => dispatchMLMetricClick('sufficiency', sufficiency.overall)}
                aria-label="Learn about Data Sufficiency"
                title="What is Data Sufficiency?"
              >
                ?
              </button>
            </h3>
            <span className={`dimension-score ${getScoreColor(sufficiency.overall)}`}>
              {Math.round(sufficiency.overall)}
            </span>
          </div>
          <div className="dimension-body">
            <div className="metric-row">
              <span className="metric-label">Volume</span>
              <span className={`metric-status status-${sufficiency.volume.status}`}>
                {sufficiency.volume.status}
              </span>
              <span className="metric-value">
                {sufficiency.volume.sampleCount.toLocaleString()} samples
              </span>
            </div>
            <div className="metric-row">
              <span className="metric-label">Coverage</span>
              <span className={`metric-status status-${sufficiency.coverage.status}`}>
                {sufficiency.coverage.status}
              </span>
              <span className="metric-value">
                {sufficiency.coverage.completeness.toFixed(1)}% complete
              </span>
            </div>
            <div className="metric-row">
              <span className="metric-label">Balance</span>
              <span className={`metric-status status-${sufficiency.balance.status}`}>
                {sufficiency.balance.status}
              </span>
              <span className="metric-value">
                {(sufficiency.balance.balanceRatio * 100).toFixed(1)}% ratio
              </span>
            </div>

            {/* Class Distribution Mini-Chart */}
            {sufficiency.balance.classDistribution && (
              <div className="class-distribution">
                <h4>Class Distribution</h4>
                <div className="distribution-bars">
                  {Object.entries(sufficiency.balance.classDistribution)
                    .slice(0, 5)
                    .map(([cls, count]) => {
                      const total = Object.values(sufficiency.balance.classDistribution!).reduce((a, b) => a + b, 0);
                      const pct = (count / total) * 100;
                      return (
                        <div key={cls} className="distribution-bar">
                          <span className="bar-label" title={cls}>
                            {cls.length > 12 ? cls.slice(0, 12) + '...' : cls}
                          </span>
                          <div className="bar-track">
                            <div
                              className="bar-fill"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="bar-value">{pct.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bias Risk Card */}
        <div className="dimension-card">
          <div className="dimension-header">
            <span className="dimension-icon">⚖️</span>
            <h3>
              Bias Risk
              <button
                type="button"
                className="ml-metric-info-btn"
                onClick={() => dispatchMLMetricClick('bias', bias.overall)}
                aria-label="Learn about Bias Risk"
                title="What is Bias Detection?"
              >
                ?
              </button>
            </h3>
            <span className={`dimension-badge ${getRiskColor(bias.riskLevel)}`}>
              {bias.riskLevel.toUpperCase()}
            </span>
          </div>
          <div className="dimension-body">
            <div className="metric-row">
              <span className="metric-label">Fairness Score</span>
              <span className={`metric-value ${getScoreColor(bias.overall)}`}>
                {Math.round(bias.overall)}/100
              </span>
            </div>
            <div className="metric-row">
              <span className="metric-label">Outcome Bias</span>
              <span className={`metric-status ${bias.outcomeBias.detected ? 'status-warning' : 'status-good'}`}>
                {bias.outcomeBias.detected ? 'Detected' : 'Not Detected'}
              </span>
            </div>
            {bias.outcomeBias.detected && (
              <div className="metric-row">
                <span className="metric-label">Max Risk Ratio</span>
                <span className="metric-value">
                  {bias.outcomeBias.maxRiskRatio.toFixed(2)}x
                </span>
              </div>
            )}

            {/* AIDRIN: TSD Fairness Metric */}
            {bias.tsd && (
              <div className="tsd-section">
                <div className="metric-row">
                  <span className="metric-label">TSD (Fairness)</span>
                  <span className={`metric-status status-${bias.tsd.interpretation.replace('_', '-')}`}>
                    {bias.tsd.interpretation.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="tsd-details">
                  <div className="tsd-gauge">
                    <div className="tsd-bar">
                      <div
                        className={`tsd-fill tsd-${bias.tsd.interpretation}`}
                        style={{ width: `${Math.min(bias.tsd.value * 200, 100)}%` }}
                      />
                    </div>
                    <span className="tsd-value">{bias.tsd.value.toFixed(3)}</span>
                  </div>
                  <small className="tsd-groups">
                    Comparing: {bias.tsd.group1_name} vs {bias.tsd.group2_name}
                  </small>
                </div>
              </div>
            )}

            {/* Phase 3: Discrimination Index (CDD Analysis) */}
            {bias.discriminationIndex && (
              <div className="discrimination-section">
                <div className="metric-row">
                  <span className="metric-label">Discrimination Index</span>
                  <span className={`metric-status status-${bias.discriminationIndex.riskLevel}`}>
                    {bias.discriminationIndex.riskLevel.charAt(0).toUpperCase() +
                     bias.discriminationIndex.riskLevel.slice(1)} Risk
                  </span>
                </div>
                <div className="discrimination-details">
                  <div className="metric-row">
                    <span className="metric-label">Max CDD</span>
                    <span className="metric-value">
                      {bias.discriminationIndex.maxCDD.toFixed(3)}
                    </span>
                  </div>
                  {bias.discriminationIndex.jsOverall != null && (
                    <div className="metric-row">
                      <span className="metric-label">JS Divergence</span>
                      <span className="metric-value">
                        {bias.discriminationIndex.jsOverall.toFixed(4)}
                      </span>
                    </div>
                  )}
                  {bias.discriminationIndex.significantAttributes.length > 0 && (
                    <div className="significant-attributes">
                      <span className="attr-label">Significant Disparities:</span>
                      <div className="attr-tags">
                        {bias.discriminationIndex.significantAttributes.map((attr, idx) => (
                          <span key={idx} className="attr-tag warning">{attr}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {bias.discriminationIndex.protectedAttributesAnalyzed.length > 0 && (
                    <small className="analyzed-attrs">
                      Analyzed: {bias.discriminationIndex.protectedAttributesAnalyzed.join(', ')}
                    </small>
                  )}
                </div>
              </div>
            )}

            {/* Proxy Variables Warning */}
            {bias.featureBias.proxyVariablesDetected.length > 0 && (
              <div className="warning-box">
                <span className="warning-icon">&#9888;</span>
                <div className="warning-content">
                  <strong>Proxy Variables Detected</strong>
                  <p>
                    {bias.featureBias.proxyVariablesDetected.slice(0, 3).join(', ')}
                    {bias.featureBias.proxyVariablesDetected.length > 3 &&
                      ` (+${bias.featureBias.proxyVariablesDetected.length - 3} more)`}
                  </p>
                </div>
              </div>
            )}

            {/* Demographics Summary */}
            {Object.keys(bias.demographics).length > 0 && (
              <div className="demographics-summary">
                <h4>Demographics</h4>
                {Object.entries(bias.demographics).slice(0, 3).map(([key, demo]) => (
                  <div key={key} className="demographic-row">
                    <span className="demo-name">{key}</span>
                    <span className={`demo-status status-${demo.status}`}>
                      {demo.status}
                    </span>
                    <span className="demo-detail">
                      {demo.dominantPercentage.toFixed(0)}% {demo.dominantCategory}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Feature Engineering Card */}
        <div className="dimension-card">
          <div className="dimension-header">
            <span className="dimension-icon">🔧</span>
            <h3>
              Feature Engineering
              <button
                type="button"
                className="ml-metric-info-btn"
                onClick={() => dispatchMLMetricClick('feature_engineering', featureEngineering.overall)}
                aria-label="Learn about Feature Engineering"
                title="What is Feature Engineering Readiness?"
              >
                ?
              </button>
            </h3>
            <span className={`dimension-score ${getScoreColor(featureEngineering.overall)}`}>
              {Math.round(featureEngineering.overall)}
            </span>
          </div>
          <div className="dimension-body">
            {/* Feature Type Breakdown */}
            <div className="feature-types">
              <div className="feature-type">
                <span className="type-icon">🔢</span>
                <span className="type-count">{featureEngineering.dataTypes.numeric.count}</span>
                <span className="type-label">Numeric</span>
              </div>
              <div className="feature-type">
                <span className="type-icon">🏷️</span>
                <span className="type-count">{featureEngineering.dataTypes.categorical.count}</span>
                <span className="type-label">Categorical</span>
              </div>
              <div className="feature-type">
                <span className="type-icon">📝</span>
                <span className="type-count">{featureEngineering.dataTypes.text.count}</span>
                <span className="type-label">Text</span>
              </div>
              <div className="feature-type">
                <span className="type-icon">📅</span>
                <span className="type-count">{featureEngineering.dataTypes.temporal.count}</span>
                <span className="type-label">Temporal</span>
              </div>
            </div>

            {/* AIDRIN: Feature Importance */}
            {featureEngineering.featureImportance && featureEngineering.featureImportance.features.length > 0 && (
              <div className="feature-importance">
                <h4>
                  <span className="section-icon">📈</span>
                  Feature Importance (Variance)
                </h4>
                <div className="importance-bars">
                  {featureEngineering.featureImportance.features.slice(0, 10).map((feat, idx) => (
                    <div key={idx} className="importance-bar">
                      <span className="bar-label" title={feat.column}>
                        {feat.column}
                      </span>
                      <div className="bar-track">
                        <div
                          className={`bar-fill ${feat.variance_importance > 0.5 ? 'high' : feat.variance_importance > 0.2 ? 'medium' : 'low'}`}
                          style={{ width: `${feat.variance_importance * 100}%` }}
                        />
                      </div>
                      <span className="bar-value">{(feat.variance_importance * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
                {featureEngineering.featureImportance.top_features.length > 0 && (
                  <div className="top-features">
                    <strong>Top features:</strong> {featureEngineering.featureImportance.top_features.join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* High Correlation Pairs */}
            {featureEngineering.highCorrelationPairs.length > 0 && (
              <div className="correlation-pairs">
                <h4>High Correlations</h4>
                {featureEngineering.highCorrelationPairs.slice(0, 3).map((pair, idx) => (
                  <div key={idx} className="correlation-row">
                    <span className="corr-columns">
                      {pair.column1} ↔ {pair.column2}
                    </span>
                    <span className={`corr-value ${Math.abs(pair.correlation) > 0.9 ? 'high' : 'medium'}`}>
                      {pair.correlation.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Feature Issues */}
            {featureEngineering.issues.length > 0 && (
              <div className="feature-issues">
                <h4>Issues ({featureEngineering.issues.length})</h4>
                {featureEngineering.issues.slice(0, 3).map((issue, idx) => (
                  <div key={idx} className={`issue-row severity-${issue.severity}`}>
                    <span className="issue-column">{issue.column}</span>
                    <span className="issue-type">{issue.issueType.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Privacy Risk Card (AIDRIN GAP-06) */}
        {privacy && (
          <div className="dimension-card privacy-card">
            <div className="dimension-header">
              <span className="dimension-icon">🔒</span>
              <h3>
                Privacy Risk
                <button
                  type="button"
                  className="ml-metric-info-btn"
                  onClick={() => dispatchMLMetricClick('privacy', privacy.overall)}
                  aria-label="Learn about Privacy Risk"
                  title="What is Privacy Risk Assessment?"
                >
                  ?
                </button>
              </h3>
              <span className={`dimension-badge ${getPrivacyRiskColor(privacy.riskLevel)}`}>
                {privacy.riskLevel.toUpperCase()}
              </span>
            </div>
            <div className="dimension-body">
              {/* Overall Privacy Score */}
              <div className="metric-row">
                <span className="metric-label">Privacy Score</span>
                <span className={`metric-value ${getScoreColor(privacy.overall)}`}>
                  {Math.round(privacy.overall)}/100
                </span>
              </div>

              {/* k-Anonymity */}
              <div className="metric-row">
                <span className="metric-label">k-Anonymity</span>
                <span className={`metric-status ${privacy.kAnonymity.pass ? 'status-good' : 'status-warning'}`}>
                  k = {privacy.kAnonymity.k} ({privacy.kAnonymity.pass ? 'PASS' : 'FAIL'})
                </span>
              </div>
              <div className="metric-row">
                <span className="metric-label">Unique Groups</span>
                <span className="metric-value">
                  {privacy.kAnonymity.uniqueCombinations.toLocaleString()}
                </span>
              </div>

              {/* Re-identification Risk */}
              <div className="privacy-reid-section">
                <h4>Re-identification Risk</h4>
                <div className="reid-risks">
                  <div className="reid-risk-row">
                    <span className="reid-label">Prosecutor</span>
                    <div className="reid-bar-track">
                      <div
                        className={`reid-bar-fill ${privacy.reidentificationRisk.prosecutorRisk > 0.5 ? 'high' : privacy.reidentificationRisk.prosecutorRisk > 0.2 ? 'medium' : 'low'}`}
                        style={{ width: `${privacy.reidentificationRisk.prosecutorRisk * 100}%` }}
                      />
                    </div>
                    <span className="reid-value">{(privacy.reidentificationRisk.prosecutorRisk * 100).toFixed(1)}%</span>
                  </div>
                  <div className="reid-risk-row">
                    <span className="reid-label">Journalist</span>
                    <div className="reid-bar-track">
                      <div
                        className={`reid-bar-fill ${privacy.reidentificationRisk.journalistRisk > 0.5 ? 'high' : privacy.reidentificationRisk.journalistRisk > 0.2 ? 'medium' : 'low'}`}
                        style={{ width: `${privacy.reidentificationRisk.journalistRisk * 100}%` }}
                      />
                    </div>
                    <span className="reid-value">{(privacy.reidentificationRisk.journalistRisk * 100).toFixed(1)}%</span>
                  </div>
                  <div className="reid-risk-row">
                    <span className="reid-label">Marketer</span>
                    <div className="reid-bar-track">
                      <div
                        className={`reid-bar-fill ${privacy.reidentificationRisk.marketerRisk > 0.5 ? 'high' : privacy.reidentificationRisk.marketerRisk > 0.2 ? 'medium' : 'low'}`}
                        style={{ width: `${privacy.reidentificationRisk.marketerRisk * 100}%` }}
                      />
                    </div>
                    <span className="reid-value">{(privacy.reidentificationRisk.marketerRisk * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              {/* High Risk Columns Warning */}
              {privacy.quasiIdentifiers.highRiskColumns.length > 0 && (
                <div className="warning-box">
                  <span className="warning-icon">&#9888;</span>
                  <div className="warning-content">
                    <strong>High-Risk Identifiers</strong>
                    <p>{privacy.quasiIdentifiers.highRiskColumns.join(', ')}</p>
                  </div>
                </div>
              )}

              {/* Quasi-Identifier Summary */}
              <div className="qi-summary">
                <div className="qi-stat high">
                  <span className="qi-count">{privacy.quasiIdentifiers.highRiskColumns.length}</span>
                  <span className="qi-label">High Risk</span>
                </div>
                <div className="qi-stat medium">
                  <span className="qi-count">{privacy.quasiIdentifiers.mediumRiskColumns.length}</span>
                  <span className="qi-label">Medium</span>
                </div>
                <div className="qi-stat low">
                  <span className="qi-count">{privacy.quasiIdentifiers.lowRiskColumns.length}</span>
                  <span className="qi-label">Low Risk</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DC-880: Include Deep Analysis Toggle */}
        {hasPhase3Data() && (
          <div className="phase3-toggle-section">
            <label className="phase3-toggle-label">
              <input
                type="checkbox"
                checked={includeDeepAnalysis}
                onChange={(e) => setIncludeDeepAnalysis(e.target.checked)}
                className="phase3-checkbox"
              />
              <span className="phase3-toggle-text">
                Include Deep Analysis
                <span className="phase3-toggle-hint">(Sample Size, Class Separability, Bias Detection, Data Point Impact)</span>
              </span>
            </label>
          </div>
        )}

        {/* Phase 3 Deep Analysis Cards - shown when checkbox is enabled */}
        {includeDeepAnalysis && hasPhase3Data() && (
          <div className="phase3-analysis-section">
            <h3 className="phase3-section-title">
              <span className="section-icon">🔬</span>
              Deep Analysis Results
            </h3>

            {/* Sample Size Adequacy Card */}
            {phase3Summary && (phase3Summary.factor50Score != null || phase3Summary.powerAnalysisScore != null) && (
              <div className="phase3-card sample-size-card">
                <div className="phase3-card-header">
                  <span className="phase3-icon">📊</span>
                  <h4>Sample Size Adequacy</h4>
                  {phase3Summary.sampleSizeAdequate != null && (
                    <span className={`phase3-badge ${phase3Summary.sampleSizeAdequate ? 'badge-good' : 'badge-warning'}`}>
                      {phase3Summary.sampleSizeAdequate ? 'Adequate' : 'Insufficient'}
                    </span>
                  )}
                </div>
                <div className="phase3-card-body">
                  <div className="phase3-metrics-row">
                    {phase3Summary.factor50Score != null && (
                      <div className="phase3-metric">
                        <span className="metric-label">Factor-50 Rule</span>
                        <span className={`metric-value ${phase3Summary.factor50Score >= 50 ? 'good' : 'warning'}`}>
                          {phase3Summary.factor50Score.toFixed(1)}:1
                        </span>
                        <span className="metric-hint">≥50:1 recommended</span>
                      </div>
                    )}
                    {phase3Summary.powerAnalysisScore != null && (
                      <div className="phase3-metric">
                        <span className="metric-label">Statistical Power</span>
                        <span className={`metric-value ${phase3Summary.powerAnalysisScore >= 80 ? 'good' : 'warning'}`}>
                          {phase3Summary.powerAnalysisScore.toFixed(0)}%
                        </span>
                        <span className="metric-hint">≥80% recommended</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Class Separability Card */}
            {classSeparability && (
              <div className="phase3-card separability-card">
                <div className="phase3-card-header">
                  <span className="phase3-icon">📈</span>
                  <h4>Class Separability</h4>
                  {classSeparability.overallScore != null && (
                    <span className={`phase3-badge ${classSeparability.overallScore >= 70 ? 'badge-good' : classSeparability.overallScore >= 50 ? 'badge-warning' : 'badge-concern'}`}>
                      {classSeparability.overallScore >= 70 ? 'Well Separated' : classSeparability.overallScore >= 50 ? 'Moderate' : 'Low Separability'}
                    </span>
                  )}
                </div>
                <div className="phase3-card-body">
                  <div className="phase3-metrics-row">
                    {classSeparability.rValueScore != null && (
                      <div className="phase3-metric">
                        <span className="metric-label">R-Value Score</span>
                        <span className={`metric-value ${classSeparability.rValueScore >= 0.7 ? 'good' : 'warning'}`}>
                          {classSeparability.rValueScore.toFixed(2)}
                        </span>
                        <span className="metric-hint">Measures class distinctness</span>
                      </div>
                    )}
                    {classSeparability.fisherRatioAvg != null && (
                      <div className="phase3-metric">
                        <span className="metric-label">Fisher Ratio (Avg)</span>
                        <span className={`metric-value ${classSeparability.fisherRatioAvg >= 1.0 ? 'good' : 'warning'}`}>
                          {classSeparability.fisherRatioAvg.toFixed(2)}
                        </span>
                        <span className="metric-hint">Higher = better separation</span>
                      </div>
                    )}
                  </div>
                  {classSeparability.separability && classSeparability.separability.length > 0 && (
                    <div className="phase3-details">
                      <button
                        className="phase3-expand-btn"
                        onClick={() => setShowPhase3Details(!showPhase3Details)}
                      >
                        {showPhase3Details ? '▼ Hide Details' : '▶ Show Class Pairs'}
                      </button>
                      {showPhase3Details && (
                        <div className="phase3-detail-list">
                          {classSeparability.separability.slice(0, 5).map((pair, idx) => (
                            <div key={idx} className="phase3-detail-item">
                              <span className="detail-label">{pair.class_pair}</span>
                              <span className="detail-value">R={(pair.r_value ?? 0).toFixed(3)}</span>
                              <span className="detail-interp">{pair.interpretation}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mislabel Detection Card */}
            {mislabelData && (
              <div className="phase3-card mislabel-card">
                <div className="phase3-card-header">
                  <span className="phase3-icon">🏷️</span>
                  <h4>Mislabel Detection</h4>
                  {mislabelData.cohensKappa != null && (
                    <span className={`phase3-badge ${mislabelData.cohensKappa >= 0.8 ? 'badge-good' : mislabelData.cohensKappa >= 0.6 ? 'badge-warning' : 'badge-concern'}`}>
                      {getKappaInterpretation(mislabelData.cohensKappa)}
                    </span>
                  )}
                </div>
                <div className="phase3-card-body">
                  <div className="phase3-metrics-row">
                    {mislabelData.cohensKappa != null && (
                      <div className="phase3-metric">
                        <span className="metric-label">Cohen's Kappa</span>
                        <span className={`metric-value ${mislabelData.cohensKappa >= 0.8 ? 'good' : 'warning'}`}>
                          {mislabelData.cohensKappa.toFixed(3)}
                        </span>
                        <span className="metric-hint">Label agreement score</span>
                      </div>
                    )}
                    {mislabelData.mislabels && (
                      <div className="phase3-metric">
                        <span className="metric-label">Suspected Mislabels</span>
                        <span className={`metric-value ${mislabelData.mislabels.length === 0 ? 'good' : 'warning'}`}>
                          {mislabelData.mislabels.length}
                        </span>
                        <span className="metric-hint">Rows to review</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Discrimination Index Card */}
            {discriminationIndex && (
              <div className="phase3-card discrimination-card">
                <div className="phase3-card-header">
                  <span className="phase3-icon">⚖️</span>
                  <h4>Discrimination Index (Bias)</h4>
                  {discriminationIndex.riskLevel && (
                    <span className={`phase3-badge badge-${discriminationIndex.riskLevel === 'low' ? 'good' : discriminationIndex.riskLevel === 'medium' ? 'warning' : 'concern'}`}>
                      {discriminationIndex.riskLevel.charAt(0).toUpperCase() + discriminationIndex.riskLevel.slice(1)} Risk
                    </span>
                  )}
                </div>
                <div className="phase3-card-body">
                  <div className="phase3-metrics-row">
                    {discriminationIndex.maxCDD != null && (
                      <div className="phase3-metric">
                        <span className="metric-label">Max CDD</span>
                        <span className={`metric-value ${discriminationIndex.maxCDD <= 0.1 ? 'good' : 'warning'}`}>
                          {discriminationIndex.maxCDD.toFixed(3)}
                        </span>
                        <span className="metric-hint">Conditional Demographic Disparity</span>
                      </div>
                    )}
                    {discriminationIndex.overallScore != null && (
                      <div className="phase3-metric">
                        <span className="metric-label">Fairness Score</span>
                        <span className={`metric-value ${discriminationIndex.overallScore >= 80 ? 'good' : 'warning'}`}>
                          {discriminationIndex.overallScore.toFixed(0)}
                        </span>
                        <span className="metric-hint">Higher = fairer model</span>
                      </div>
                    )}
                  </div>
                  {discriminationIndex.significantDisparities && discriminationIndex.significantDisparities.length > 0 && (
                    <div className="phase3-warning">
                      <span className="warning-icon">⚠️</span>
                      <span className="warning-text">
                        Significant disparities detected in: {discriminationIndex.significantDisparities.map(d => d.protected_attribute).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Data Point Impact Card */}
            {dataPointImpact && (
              <div className="phase3-card impact-card">
                <div className="phase3-card-header">
                  <span className="phase3-icon">🎯</span>
                  <h4>Data Point Impact</h4>
                  {dataPointImpact.highInfluenceCount != null && (
                    <span className={`phase3-badge ${dataPointImpact.highInfluenceCount === 0 ? 'badge-good' : dataPointImpact.highInfluenceCount <= 5 ? 'badge-warning' : 'badge-concern'}`}>
                      {dataPointImpact.highInfluenceCount} High Influence Points
                    </span>
                  )}
                </div>
                <div className="phase3-card-body">
                  <div className="phase3-metrics-row">
                    <div className="phase3-metric">
                      <span className="metric-label">High Influence</span>
                      <span className={`metric-value ${(dataPointImpact.highInfluenceCount || 0) === 0 ? 'good' : 'warning'}`}>
                        {dataPointImpact.highInfluenceCount || 0}
                      </span>
                      <span className="metric-hint">Points with outsized impact</span>
                    </div>
                  </div>
                  {dataPointImpact.highInfluencePoints && dataPointImpact.highInfluencePoints.length > 0 && (
                    <div className="phase3-detail-list">
                      <p className="detail-header">Top Influential Points:</p>
                      {dataPointImpact.highInfluencePoints.slice(0, 3).map((point, idx) => (
                        <div key={idx} className="phase3-detail-item">
                          <span className="detail-label">Row {point.row_index}</span>
                          <span className="detail-value">Influence: {(point.influence_score ?? 0).toFixed(3)}</span>
                          {point.shapley_value != null && (
                            <span className="detail-shapley">Shapley: {point.shapley_value.toFixed(3)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="ml-recommendations">
          <h3>
            <span className="section-icon">💡</span>
            ML Readiness Recommendations
          </h3>
          <div className="recommendations-list">
            {recommendations.slice(0, 5).map((rec, idx) => (
              <div key={idx} className={`recommendation-item priority-${rec.priority}`}>
                <div className="rec-header">
                  <span className="rec-priority">{getPriorityIcon(rec.priority)}</span>
                  <span className="rec-category">{getCategoryIcon(rec.category)}</span>
                  <span className="rec-title">{rec.title}</span>
                </div>
                <p className="rec-description">{rec.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MLReadinessResults;
